import { c as _c } from "react/compiler-runtime";
import { Hint, PrimaryButton, toast, useCustomToasts } from '@systemeio/ui';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import SettingsLayout from 'modules/profile/components/settings-layout';
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys';
import { PublicApiSettingsLoco } from 'modules/profile/public-api-settings/constants/public-api-settings-loco';
import { useCreateDataPublicApiKeys } from 'modules/profile/public-api-settings/hooks/use-create-data-public-api-keys';
import { useDeleteKey } from 'modules/profile/public-api-settings/hooks/use-delete-key';
import { usePublicKeys } from 'modules/profile/public-api-settings/hooks/use-public-keys';
import { KeyTypeEnum, PublicApiSettingsCreatedInterface, PublicApiSettingsViewInterface } from 'modules/profile/public-api-settings/types/public-api-settings-interface';
import { isPublicApiSettingsAllowed } from 'modules/profile/public-api-settings/utils/is-public-api-settings-allowed';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { CopyToClipboardButton } from 'shared/components/copy-to-clipboard-button';
import CreateModal from 'shared/components/create-modal';
import { InfoBlockOrderedListItem } from 'shared/components/info-block-ordered-list-item';
import { StatusCell } from 'shared/components/table/cells/status-cell';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { AddIcon } from '@systemeio/ui/icons';
import { McpKeys } from 'modules/profile/public-api-settings/components/mcp-keys';
import SettingsSectionHeading from 'modules/profile/components/settings-section-heading';
import { GrowthbookFlagsEnum } from 'growthbook/types';
const PublicApiSettingsSettings: NextPage = () => {
  const $ = _c(137);
  const {
    t
  } = useLocoTranslation();
  const router = useRouter();
  const mcpKeysIsOn = useFeatureIsOn(GrowthbookFlagsEnum.McpKeys);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [newCreatedKeys, setNewCreatedKeys] = useState(t0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateFetching, setIsCreateFetching] = useState(false);
  const [infoToastId, setInfoToastId] = useState(null);
  const {
    infoToast
  } = useCustomToasts();
  const {
    user,
    isWsAdmin,
    isWorkspaceOwner
  } = useUser();
  const {
    apiKeys,
    mutateApiKeys
  } = usePublicKeys();
  const {
    deleteKey
  } = useDeleteKey();
  let t1;
  if ($[1] !== infoToast || $[2] !== infoToastId || $[3] !== mutateApiKeys || $[4] !== t) {
    t1 = {
      onCreate: async createdKey => {
        await mutateApiKeys(data => data && [...data, {
          id: createdKey.id,
          name: createdKey.name,
          createdAt: createdKey.createdAt,
          expiresAt: createdKey.expiresAt,
          maskedToken: createdKey.token,
          expired: false,
          type: KeyTypeEnum.Api
        }], false);
        setNewCreatedKeys(prev => [...prev, {
          id: createdKey.id,
          token: createdKey.token
        }]);
        infoToastId && toast.dismiss(infoToastId);
        setInfoToastId(infoToast(t("dashboard.user_settings.public_api.copy_reminder")));
      }
    };
    $[1] = infoToast;
    $[2] = infoToastId;
    $[3] = mutateApiKeys;
    $[4] = t;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    createData
  } = useCreateDataPublicApiKeys(t1);
  let t2;
  if ($[6] !== deleteKey || $[7] !== mutateApiKeys) {
    t2 = async deletedItem => {
      await deleteKey(deletedItem.id);
      await mutateApiKeys(data_0 => data_0 && data_0?.filter(t3 => {
        const {
          id
        } = t3;
        return id !== deletedItem.id;
      }), false);
      setNewCreatedKeys(prev_0 => prev_0.filter(token => token.id !== deletedItem.id));
    };
    $[6] = deleteKey;
    $[7] = mutateApiKeys;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const onDeletePublicApiKeys = t2;
  let t3;
  let t4;
  if ($[9] !== isWorkspaceOwner || $[10] !== isWsAdmin || $[11] !== router || $[12] !== user) {
    t3 = () => {
      if (user && !isPublicApiSettingsAllowed({
        user,
        isWsAdmin,
        isWorkspaceOwner
      })) {
        router.replace("/");
      }
    };
    t4 = [isWsAdmin, isWorkspaceOwner, router, user];
    $[9] = isWorkspaceOwner;
    $[10] = isWsAdmin;
    $[11] = router;
    $[12] = user;
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  useEffect(t3, t4);
  let t5;
  if ($[15] !== t) {
    t5 = t("dashboard.user_settings.public_api.name_tooltip");
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t) {
    t6 = t("dashboard.user_settings.public_api.name");
    $[17] = t;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = {
      key: "name",
      header: <Hint label={t5}>{t6}</Hint>,
      widthPercent: 20
    };
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t) {
    t8 = t("dashboard.user_settings.public_api.token_tooltip");
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t) {
    t9 = t("dashboard.user_settings.public_api.token");
    $[24] = t;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t8 || $[27] !== t9) {
    t10 = <Hint label={t8}>{t9}</Hint>;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== infoToastId || $[30] !== newCreatedKeys) {
    t11 = t12 => {
      const {
        id: id_0,
        maskedToken
      } = t12;
      const newCreatedKey = newCreatedKeys.find(key => key.id == id_0);
      return newCreatedKey ? <div className="flex gap-2"><CopyToClipboardButton text={newCreatedKeys.find(key_0 => key_0.id == id_0)?.token || ""} className="shrink-0" onCopy={() => infoToastId && toast.dismiss(infoToastId)} /><p className="truncate text-sm">{newCreatedKey.token}</p></div> : <p className="truncate text-sm">{maskedToken}</p>;
    };
    $[29] = infoToastId;
    $[30] = newCreatedKeys;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11) {
    t12 = {
      key: "maskedToken",
      header: t10,
      widthPercent: 50,
      onRender: t11
    };
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t) {
    t13 = t("global.status");
    $[35] = t;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t13) {
    t14 = {
      key: "status",
      header: t13,
      widthPercent: 10,
      onRender: _temp
    };
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t) {
    t15 = t("dashboard.user_settings.public_api.expires_at");
    $[39] = t;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t || $[42] !== user?.dateFormat || $[43] !== user?.timeFormat || $[44] !== user?.timezone) {
    t16 = t17 => {
      const {
        expiresAt
      } = t17;
      return expiresAt === null ? t("dashboard.user_settings.public_api.expires_at_never") : dateTimeFormat(expiresAt, {
        timeFormat: user?.timeFormat,
        dateFormat: user?.dateFormat,
        timeZone: user?.timezone
      });
    };
    $[41] = t;
    $[42] = user?.dateFormat;
    $[43] = user?.timeFormat;
    $[44] = user?.timezone;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t15 || $[47] !== t16) {
    t17 = {
      key: "expiresAt",
      header: t15,
      widthPercent: 20,
      onRender: t16
    };
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t12 || $[50] !== t14 || $[51] !== t17 || $[52] !== t7) {
    t18 = [t7, t12, t14, t17];
    $[49] = t12;
    $[50] = t14;
    $[51] = t17;
    $[52] = t7;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  const columns = t18;
  let t19;
  if ($[54] !== mcpKeysIsOn || $[55] !== t) {
    t19 = mcpKeysIsOn ? t(settingsMenuLocoKeys.public_api) : t("dashboard.user_settings.public_api.title_plural");
    $[54] = mcpKeysIsOn;
    $[55] = t;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t19) {
    t20 = [{
      caption: t19,
      isStatic: true
    }];
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      a: <a className="primary-link" target="_blank" rel="noreferrer" href="https://api.systeme.io/documentation" />
    };
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  let t23;
  if ($[60] !== t) {
    t22 = <TransLoco t={t} i18nKey="dashboard.user_settings.public_api.title_tooltip" components={t21} />;
    t23 = t("dashboard.user_settings.public_api.title_plural");
    $[60] = t;
    $[61] = t22;
    $[62] = t23;
  } else {
    t22 = $[61];
    t23 = $[62];
  }
  let t24;
  if ($[63] !== t23) {
    t24 = <h3>{t23}</h3>;
    $[63] = t23;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t22 || $[66] !== t24) {
    t25 = <SettingsSectionHeading><Hint label={t22}>{t24}</Hint></SettingsSectionHeading>;
    $[65] = t22;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t) {
    t26 = t("dashboard.user_settings.public_api.instruction_title");
    $[68] = t;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== t26) {
    t27 = <h4 className="text-text-primary text-[16px] font-bold leading-6 ">{t26}</h4>;
    $[70] = t26;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== t) {
    t28 = t("dashboard.user_settings.public_api.instruction_first");
    $[72] = t;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  let t29;
  if ($[74] !== t28) {
    t29 = <InfoBlockOrderedListItem wrapperClassName="items-center">{t28}</InfoBlockOrderedListItem>;
    $[74] = t28;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== t) {
    t30 = t("dashboard.user_settings.public_api.instruction_second");
    $[76] = t;
    $[77] = t30;
  } else {
    t30 = $[77];
  }
  let t31;
  if ($[78] !== t30) {
    t31 = <InfoBlockOrderedListItem wrapperClassName="items-center">{t30}</InfoBlockOrderedListItem>;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== t29 || $[81] !== t31) {
    t32 = <ul className="flex flex-col gap-6">{t29}{t31}</ul>;
    $[80] = t29;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      b: <b />
    };
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== t) {
    t34 = <p><TransLoco t={t} i18nKey="dashboard.user_settings.public_api.instruction_warning" components={t33} /></p>;
    $[84] = t;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== t25 || $[87] !== t27 || $[88] !== t32 || $[89] !== t34) {
    t35 = <div className={"flex h-fit flex-col gap-6\n          xl:col-start-2 xl:row-start-1 xl:row-end-7"}>{t25}{t27}{t32}{t34}</div>;
    $[86] = t25;
    $[87] = t27;
    $[88] = t32;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== t) {
    t36 = t("dashboard.user_settings.public_api.title");
    $[91] = t;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] !== onDeletePublicApiKeys) {
    t37 = {
      remove: {
        onChange: onDeletePublicApiKeys
      }
    };
    $[93] = onDeletePublicApiKeys;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  let t38;
  if ($[95] !== apiKeys) {
    t38 = apiKeys && {
      hasMore: false,
      items: apiKeys
    };
    $[95] = apiKeys;
    $[96] = t38;
  } else {
    t38 = $[96];
  }
  let t39;
  if ($[97] !== columns || $[98] !== t36 || $[99] !== t37 || $[100] !== t38) {
    t39 = <Table caption={t36} actions={t37} data={t38} columns={columns} emptyListWithTitles={true} />;
    $[97] = columns;
    $[98] = t36;
    $[99] = t37;
    $[100] = t38;
    $[101] = t39;
  } else {
    t39 = $[101];
  }
  const t40 = createData.disabled ? createData.disabled : !apiKeys;
  let t41;
  if ($[102] !== createData) {
    t41 = () => {
      if (createData.onOpen) {
        setIsCreateFetching(true);
        createData.onOpen().then(() => {
          setIsCreateFetching(false);
          setIsModalOpen(true);
        });
      } else {
        setIsModalOpen(true);
      }
    };
    $[102] = createData;
    $[103] = t41;
  } else {
    t41 = $[103];
  }
  const t42 = createData.testAttributePostfix;
  let t43;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <AddIcon />;
    $[104] = t43;
  } else {
    t43 = $[104];
  }
  let t44;
  if ($[105] !== t) {
    t44 = t("global.create");
    $[105] = t;
    $[106] = t44;
  } else {
    t44 = $[106];
  }
  let t45;
  if ($[107] !== t44) {
    t45 = <span> {t44}</span>;
    $[107] = t44;
    $[108] = t45;
  } else {
    t45 = $[108];
  }
  let t46;
  if ($[109] !== createData.testAttributePostfix || $[110] !== isCreateFetching || $[111] !== t40 || $[112] !== t41 || $[113] !== t45) {
    t46 = <PrimaryButton disabled={t40} onClick={t41} isFetching={isCreateFetching} className="flex" testAttributePostfix={t42}>{t43}{t45}</PrimaryButton>;
    $[109] = createData.testAttributePostfix;
    $[110] = isCreateFetching;
    $[111] = t40;
    $[112] = t41;
    $[113] = t45;
    $[114] = t46;
  } else {
    t46 = $[114];
  }
  let t47;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = () => {
      setIsModalOpen(false);
    };
    $[115] = t47;
  } else {
    t47 = $[115];
  }
  let t48;
  if ($[116] !== createData) {
    t48 = data_1 => createData.onCreate(data_1);
    $[116] = createData;
    $[117] = t48;
  } else {
    t48 = $[117];
  }
  let t49;
  if ($[118] !== createData || $[119] !== isModalOpen || $[120] !== t48) {
    t49 = <CreateModal opened={isModalOpen} onClose={t47} afterLeave={createData.onAfterLeave} {...createData} onCreate={t48} />;
    $[118] = createData;
    $[119] = isModalOpen;
    $[120] = t48;
    $[121] = t49;
  } else {
    t49 = $[121];
  }
  let t50;
  if ($[122] !== t46 || $[123] !== t49) {
    t50 = <div className="my-4 flex justify-center">{t46}{t49}</div>;
    $[122] = t46;
    $[123] = t49;
    $[124] = t50;
  } else {
    t50 = $[124];
  }
  let t51;
  if ($[125] !== t39 || $[126] !== t50) {
    t51 = <div className="bg-background-secondary border-text-secondary/30 mt-5 h-fit w-full min-w-0 rounded-lg border">{t39}{t50}</div>;
    $[125] = t39;
    $[126] = t50;
    $[127] = t51;
  } else {
    t51 = $[127];
  }
  let t52;
  if ($[128] !== mcpKeysIsOn) {
    t52 = mcpKeysIsOn && <McpKeys />;
    $[128] = mcpKeysIsOn;
    $[129] = t52;
  } else {
    t52 = $[129];
  }
  let t53;
  if ($[130] !== t35 || $[131] !== t51 || $[132] !== t52) {
    t53 = <div className="bg-background-secondary flex w-full min-w-0 flex-col rounded-md p-3 lg:p-6">{t35}{t51}{t52}</div>;
    $[130] = t35;
    $[131] = t51;
    $[132] = t52;
    $[133] = t53;
  } else {
    t53 = $[133];
  }
  let t54;
  if ($[134] !== t20 || $[135] !== t53) {
    t54 = <SettingsLayout needSave={false} titles={t20}>{t53}</SettingsLayout>;
    $[134] = t20;
    $[135] = t53;
    $[136] = t54;
  } else {
    t54 = $[136];
  }
  return t54;
};
export const getServerSideProps = getBaseServerSideProps(PublicApiSettingsLoco, 'global.settings');
export default PublicApiSettingsSettings;
function _temp(t0) {
  const {
    expired
  } = t0;
  return <div className="flex justify-center gap-2"><StatusCell isActive={!expired} className="shrink-0" /></div>;
}